import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { MagnifyingGlass, Plus, Pencil, Trash, Cube, X } from '@phosphor-icons/react';

const API = '/api';

const AVIATION_CATEGORIES = [
  'Fuselage', 'Wing', 'Empennage', 'Engine', 'Landing Gear', 'Flight Controls',
  'Hydraulics', 'Avionics', 'Fuel System', 'Electrical', 'APU', 'Cabin',
  'Environmental', 'Fire Protection', 'Rotor System'
];

const MARITIME_CATEGORIES = [
  'Hull', 'Propulsion', 'Superstructure', 'Deck', 'Deck Equipment',
  'Navigation', 'Safety Equipment'
];

function PartFormDialog({ open, onClose, part, onSave }) {
  const [form, setForm] = useState({
    name: '', category: '', subcategory: '', sector: 'aviation',
    description: '', part_number: '', aircraft_types: [], vessel_types: [],
    visual_cues: [], keywords: [],
  });
  const [cueInput, setCueInput] = useState('');
  const [kwInput, setKwInput] = useState('');
  const [typeInput, setTypeInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (part) {
      setForm({
        name: part.name || '',
        category: part.category || '',
        subcategory: part.subcategory || '',
        sector: part.sector || 'aviation',
        description: part.description || '',
        part_number: part.part_number || '',
        aircraft_types: part.aircraft_types || [],
        vessel_types: part.vessel_types || [],
        visual_cues: part.visual_cues || [],
        keywords: part.keywords || [],
      });
    } else {
      setForm({ name: '', category: '', subcategory: '', sector: 'aviation', description: '', part_number: '', aircraft_types: [], vessel_types: [], visual_cues: [], keywords: [] });
    }
  }, [part, open]);

  const addTag = (field, value, setInput) => {
    const v = value.trim();
    if (v && !form[field].includes(v)) {
      setForm({ ...form, [field]: [...form[field], v] });
    }
    setInput('');
  };

  const removeTag = (field, value) => {
    setForm({ ...form, [field]: form[field].filter(t => t !== value) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error saving part');
    } finally {
      setSaving(false);
    }
  };

  const categories = form.sector === 'maritime' ? MARITIME_CATEGORIES : AVIATION_CATEGORIES;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Outfit' }}>{part ? 'Edit Part' : 'Add New Part'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-lg" data-testid="part-name-input" />
            </div>
            <div>
              <Label>Part Number</Label>
              <Input value={form.part_number} onChange={(e) => setForm({ ...form, part_number: e.target.value })} className="rounded-lg" data-testid="part-number-input" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Sector *</Label>
              <Select value={form.sector} onValueChange={(v) => setForm({ ...form, sector: v, category: '' })}>
                <SelectTrigger className="rounded-lg" data-testid="part-sector-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="aviation">Aviation</SelectItem>
                  <SelectItem value="maritime">Maritime</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="rounded-lg" data-testid="part-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subcategory</Label>
              <Input value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} className="rounded-lg" data-testid="part-subcategory-input" />
            </div>
          </div>

          <div>
            <Label>Description</Label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full min-h-[80px] px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="part-description-input"
            />
          </div>

          {/* Visual Cues */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Visual Cues (for AI identification)</Label>
            <div className="flex gap-2 mt-1">
              <Input value={cueInput} onChange={(e) => setCueInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag('visual_cues', cueInput, setCueInput); } }} placeholder="e.g., cylindrical shape, metal surface..." className="rounded-lg" data-testid="visual-cue-input" />
              <Button type="button" variant="outline" size="sm" onClick={() => addTag('visual_cues', cueInput, setCueInput)} className="rounded-lg shrink-0">Add</Button>
            </div>
            {form.visual_cues.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {form.visual_cues.map(c => (
                  <span key={c} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">
                    {c}
                    <button type="button" onClick={() => removeTag('visual_cues', c)} className="hover:text-red-600"><X size={10} weight="bold" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Keywords */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Keywords</Label>
            <div className="flex gap-2 mt-1">
              <Input value={kwInput} onChange={(e) => setKwInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag('keywords', kwInput, setKwInput); } }} placeholder="Search keywords..." className="rounded-lg" data-testid="keyword-input" />
              <Button type="button" variant="outline" size="sm" onClick={() => addTag('keywords', kwInput, setKwInput)} className="rounded-lg shrink-0">Add</Button>
            </div>
            {form.keywords.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {form.keywords.map(k => (
                  <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-800">
                    {k}
                    <button type="button" onClick={() => removeTag('keywords', k)} className="hover:text-red-600"><X size={10} weight="bold" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Aircraft/Vessel Types */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">{form.sector === 'aviation' ? 'Aircraft Types' : 'Vessel Types'}</Label>
            <div className="flex gap-2 mt-1">
              <Input value={typeInput} onChange={(e) => setTypeInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(form.sector === 'aviation' ? 'aircraft_types' : 'vessel_types', typeInput, setTypeInput); } }} placeholder={form.sector === 'aviation' ? 'e.g., Boeing 737, Airbus A320...' : 'e.g., Tanker, Cargo Ship...'} className="rounded-lg" data-testid="type-input" />
              <Button type="button" variant="outline" size="sm" onClick={() => addTag(form.sector === 'aviation' ? 'aircraft_types' : 'vessel_types', typeInput, setTypeInput)} className="rounded-lg shrink-0">Add</Button>
            </div>
            {(form.sector === 'aviation' ? form.aircraft_types : form.vessel_types).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {(form.sector === 'aviation' ? form.aircraft_types : form.vessel_types).map(t => (
                  <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-800">
                    {t}
                    <button type="button" onClick={() => removeTag(form.sector === 'aviation' ? 'aircraft_types' : 'vessel_types', t)} className="hover:text-red-600"><X size={10} weight="bold" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" disabled={saving} className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg" data-testid="part-save-btn">
              {saving ? 'Saving...' : (part ? 'Update Part' : 'Create Part')}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-lg">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function PartsManagement() {
  const { user } = useAuth();
  const [parts, setParts] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editPart, setEditPart] = useState(null);

  const loadParts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '30' });
      if (search) params.append('search', search);
      if (sectorFilter) params.append('sector', sectorFilter);
      if (categoryFilter) params.append('category', categoryFilter);
      const { data } = await axios.get(`${API}/parts?${params}`, { withCredentials: true });
      setParts(data.items);
      setTotal(data.total);
      setCategories(data.categories);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadParts(); }, [page, sectorFilter, categoryFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadParts();
  };

  const handleSave = async (formData) => {
    if (editPart) {
      await axios.put(`${API}/parts/${editPart.part_id}`, formData, { withCredentials: true });
    } else {
      await axios.post(`${API}/parts`, formData, { withCredentials: true });
    }
    loadParts();
  };

  const handleDelete = async (partId) => {
    if (!window.confirm('Are you sure you want to delete this part?')) return;
    try {
      await axios.delete(`${API}/parts/${partId}`, { withCredentials: true });
      loadParts();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error deleting part');
    }
  };

  const totalPages = Math.ceil(total / 30);

  return (
    <div data-testid="parts-management-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Parts Database</h1>
          <p className="text-slate-500 mt-1">Manage aircraft and vessel parts for AR identification ({total} parts)</p>
        </div>
        {user?.role === 'admin' && (
          <Button
            onClick={() => { setEditPart(null); setDialogOpen(true); }}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg"
            data-testid="add-part-btn"
          >
            <Plus size={18} className="mr-2" />
            Add Part
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
            <Label className="text-xs text-slate-500">Search</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search parts..."
                  className="pl-9 rounded-lg"
                  data-testid="parts-search-input"
                />
              </div>
              <Button type="submit" variant="outline" className="rounded-lg" data-testid="parts-search-btn">
                <MagnifyingGlass size={16} />
              </Button>
            </div>
          </form>
          <div className="w-40">
            <Label className="text-xs text-slate-500">Sector</Label>
            <Select value={sectorFilter} onValueChange={(v) => { setSectorFilter(v === 'all' ? '' : v); setCategoryFilter(''); setPage(1); }}>
              <SelectTrigger className="rounded-lg" data-testid="sector-filter">
                <SelectValue placeholder="All Sectors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sectors</SelectItem>
                <SelectItem value="aviation">Aviation</SelectItem>
                <SelectItem value="maritime">Maritime</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-48">
            <Label className="text-xs text-slate-500">Category</Label>
            <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="rounded-lg" data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Parts Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500">Loading parts...</div>
        ) : parts.length === 0 ? (
          <div className="p-12 text-center">
            <Cube size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No parts found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Part Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Sector</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Visual Cues</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Keywords</th>
                    {user?.role === 'admin' && (
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {parts.map((p) => (
                    <tr key={p.part_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`part-row-${p.part_id}`}>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-slate-900">{p.name}</div>
                        {p.subcategory && <div className="text-xs text-slate-500">{p.subcategory}</div>}
                        {p.part_number && <div className="text-xs text-slate-400 font-mono">{p.part_number}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">{p.category}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.sector === 'aviation' ? 'bg-blue-100 text-blue-800' : 'bg-cyan-100 text-cyan-800'}`}>
                          {p.sector === 'aviation' ? 'Aviation' : 'Maritime'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {(p.visual_cues || []).slice(0, 2).map(c => (
                            <span key={c} className="px-1.5 py-0.5 rounded text-[10px] bg-blue-50 text-blue-700">{c}</span>
                          ))}
                          {(p.visual_cues || []).length > 2 && <span className="text-[10px] text-slate-400">+{p.visual_cues.length - 2}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1 max-w-[150px]">
                          {(p.keywords || []).slice(0, 2).map(k => (
                            <span key={k} className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-700">{k}</span>
                          ))}
                          {(p.keywords || []).length > 2 && <span className="text-[10px] text-slate-400">+{p.keywords.length - 2}</span>}
                        </div>
                      </td>
                      {user?.role === 'admin' && (
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={() => { setEditPart(p); setDialogOpen(true); }} className="rounded-lg" data-testid={`edit-part-${p.part_id}`}>
                              <Pencil size={14} />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleDelete(p.part_id)} className="rounded-lg text-red-600 hover:bg-red-50" data-testid={`delete-part-${p.part_id}`}>
                              <Trash size={14} />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t border-slate-200">
                <p className="text-sm text-slate-500">Page {page} of {totalPages} ({total} parts)</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-lg">Previous</Button>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="rounded-lg">Next</Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <PartFormDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditPart(null); }}
        part={editPart}
        onSave={handleSave}
      />
    </div>
  );
}
